<template>
  <div class="mt-4">
    <label class="text-left block font-bold">Transportation Details</label>
    <div class="grid grid-cols-2 gap-7">
      <ValidationProvider rules="required|afterNow" name="start date" v-slot="{ errors }">
        <CalendarField 
          v-model="formValues.startDate"
          @input="updateValue"
          @change="updateValue"
          :min="minRequestDate()"
        >
          Start Date
        </CalendarField>
        <span
          class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
          name="error-text">
          {{ errors[0] }}
        </span>
        <span v-if="startDateSchoolYearWarning"
          class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
          {{ startDateSchoolYearWarning }}
        </span>
      </ValidationProvider>
      <div v-if="isSummerSession()">
        <ValidationProvider :rules="`required|afterDate:${formValues.startDate}`" name="end date" v-slot="{ errors }">
          <CalendarField  
            v-model="formValues.endDate" 
            :min="formValues.startDate ? formValues.startDate : minRequestDate()"
          >
            Last Day of Transportation
          </CalendarField>
          <span
            class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
            name="error-text">
            {{ errors[0] }}
          </span>
        </ValidationProvider>
        <span class="text-xs">
          Only for summer transportation.
        </span>
      </div>
    </div>
    <div class="flex flex-row items-end mt-2">
      <div class="grow">
        <div class="flex flex-row items-baseline">
          <h6>School or Site Destination Name</h6>
          <Spinner
            v-if="displayLoadingBuildings"
            :size="6"
          />
        </div>
        <ValidationProvider rules="required" name="school or program name" v-slot="{ errors }">
          <v-select
            class="border-gray-300"
            :options="schoolList"
            label="name"
            taggable
            v-model="selectedSchool"
            @input="setSchool()"
            placeholder="Please select or type school or site destination name and then PRESS ENTER">
          </v-select>
          <span
            class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
            name="error-text">
            {{ errors[0] }}
          </span>
        </ValidationProvider>
      </div>
    </div>
    <span class="text-xs">
      Please select or type school or site destination name and then PRESS ENTER
    </span>
    <InputError 
      class="-mt-2" 
      :label="'School or Program Name is required'"
      :condition="selectedSchool === '' && schoolIsEdited" 
    />
    <SchoolAddressDisplay 
      v-if="schoolExists() === true"
      :value="selectedSchool.address" 
    />
    <StudentAddress
      v-if="schoolExists() === false"
      name="schoolAddress"
      label="School Address"
      v-model="selectedSchool.address"
      @input="setSchool(); $refs.suggestedSchoolForm.showSuggestedSchools(selectedSchool)"
      @loseFocus="onSchoolAddressLoseFocus"
      :disableDefaultValueUpdates="defaultValueComponentUpdatedMap['schoolAddress']"
      :defaultValue="defaultSchoolAddress"
      @defaultValueUsed="defaultValueUsed('schoolAddress')"
    />
    <!--SchoolAddressDisplay 
      v-if="selectedSchool &&
      schoolList &&
      schoolList.map(a => a.name).includes(selectedSchool.name)" 
      :value="selectedSchool.address" 
    />
    <StudentAddress
      v-if="selectedSchool && schoolList && !schoolList.map(a => a.name).includes(selectedSchool.name)"
      name="schoolAddress" 
      label="School Address"
      v-model="selectedSchool.address"
      @loseFocus="onSchoolAddressLoseFocus"
    /-->
    <label class="text-left block text-xl font-bold mt-4">Transportation Time and Day</label>
    <span v-if="(daysIsEdited || attemptedSubmit) && (formValues.inboundDays?.length < 1) && (formValues.outboundDays?.length < 1)"
      class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
      name="error-text">
      At least one transportation day is required
    </span>
    <label class="text-left block font-bold text-lg mt-2">
      Select INbound transportation days (TO the school/site)
    </label>
    <span class="text-xs -mt-1">
      If the student follows a complicated hybrid scenario (like weekends or alternating weeks), please check OTHER and explain.
    </span>
    <!--ValidationProvider :rules="`twoArraysNotEmpty:${formValues.outboundDays?.length}`" name="transportation day" v-slot="{ errors }"-->
    <!--ValidationProvider rules="required" name="single transportation day" v-slot="{ errors }">
      <input type="hidden" :value="formValues.inboundDays?.concat(formValues.outboundDays)">
      <span
        class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
        name="error-text">
        {{ errors[0] }}
      </span>
    </ValidationProvider-->
    <div class="flex flex-row mt-1 mb-3 items-end">
      <label class="mr-2 pr-2 pb-0.5">
        <input 
          type="checkbox" 
          v-model="allInDaysSelected" 
          @change="toggleAllInbound()" 
        />
        {{ allInDaysSelected ? 'Un-select All' : 'Select All' }}
      </label>
      <MultiCheckboxField 
        :options="weekDays" 
        v-model="formValues.inboundDays" 
        @input="blurDays(); checkOther('in'); checkSelectAll('out')"
        :required="true"
      />
        <label v-if="otherDaySelectedIn" class="mr-2 pr-2 pb-0.5">
          <input 
            type="checkbox" 
            @input="toggleInboundWeekend('Saturday')"
            :checked="formValues.inboundDays.includes('Saturday')"
          />
          Saturday
        </label>
        <label v-if="otherDaySelectedIn" class="mr-2 pr-2 pb-0.5">
          <input 
            type="checkbox" 
            @input="toggleInboundWeekend('Sunday')"
            :checked="formValues.inboundDays.includes('Sunday')"
          />
          Sunday
        </label>
    </div>
    <div v-if="formValues.inboundDays?.length > 0">
      <div class="grid grid-cols-2 gap-7">
        <ValidationProvider rules="required" name="start time" v-slot="{ errors }">
          <TimeField
            :defaultValue="getDefaultValueForTime('arrivalTime', defaultValue.startTime)"
            :disableDefaultValueUpdates="defaultValueComponentUpdatedMap['startTime']"
            @defaultValueUsed="defaultValueUsed('startTime')"
            class="w-full"
            v-model="formValues.startTime"
            @input="subtractArrivalTime('arrivalTime', -10)"
          >
            School or Site Start Time
          </TimeField>
          <span
            class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
            name="error-text">
            {{ errors[0] }}
          </span>
        </ValidationProvider>
        <div>
          <ValidationProvider rules="required" name="intended arrival time" v-slot="{ errors }">
            <TimeField
              :defaultValue="getDefaultValueForTime(undefined, defaultValue.arrivalTime)"
              :disableDefaultValueUpdates="defaultValueComponentUpdatedMap['arrivalTime']"
              @defaultValueUsed="defaultValueUsed('arrivalTime')"
              class="w-full"
              v-model="formValues.arrivalTime"
            >
              Intended Arrival Time
            </TimeField>
            <span
              class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
              name="error-text">
              {{ errors[0] }}
            </span>
          </ValidationProvider>
          <span class="text-xs">
            Default to arrive 10 minutes prior to the start time.
          </span>
        </div>
      </div>
      <div class="flex flex-col"
        :key="transOptionsInKey">
        <label class="text-sm font-bold mt-2"
          id="Pick-up">
          <input
            type="checkbox"
            :disabled="formValues.diffPickupTimes"
            :checked="diffPickupAddress"
            @change="togglePickup()"
          />
          Check here if the pick-up address is not the student home address.
        </label>
        </div>
      <StudentAddress
        class="-mt-4"
        v-if="diffPickupAddress && !formValues.diffPickupTimes"
        v-model="formValues.newPickupAddress" 
        :label="'New Pick Up Address'" 
        :defaultValue="defaultPickupAddress" 
        :disableDefaultValueUpdates="defaultValueComponentUpdatedMap['diffPickupAddress']"
        @defaultValueUsed="defaultValueUsed('diffPickupAddress')"
        @input="resetReviewed()"
      />
        <label class="text-sm font-bold mt-2">
          <input 
            type="checkbox" 
            :checked="formValues.diffPickupTimes"
            @change="toggleDifferentDays('in')"
            />
            Check here if some days have a different pick-up time or address
          </label>
          <div
            v-if="formValues.diffPickupTimes"
            class="flex flex-col">
            <DifferentTransOptions
              :daysList="formValues.inboundDays"
              v-model="formValues.inboundDayDifferent"
              :defaultValue="defaultValueInboundDayDifferent"
              :disableDefaultValueUpdates="defaultValueComponentUpdatedMap['diffPickupDaysTransOptions']"
              @defaultValueUsed="defaultValueUsed('diffPickupDaysTransOptions')"
              direction="in"
            />            
          </div>
      <TextArea
        v-model="formValues.inboundInfo">
        Specific Dropoff Information for INbound Route (TO school/site)
      </TextArea>
      <span class="text-xs">
        ex. DROPOFF DOOR at school/site.
      </span>
    </div>
    <label class="text-left block font-bold text-lg mt-4">
      Select OUTbound transportation days (FROM the school/facility)
    </label>
    <span class="text-xs -mt-1">
      If the student follows a complicated hybrid scenario (like weekends or alternating weeks), please check OTHER and explain.
    </span>
    <div class="flex flex-row mt-1 mb-3 items-end">
      <label class="mr-2 pr-2">
        <input 
          type="checkbox" 
          v-model="allOutDaysSelected" 
          @change="toggleAllOutbound()" 
        />
        {{ allOutDaysSelected ? 'Un-select All' : 'Select All' }}
      </label>
      <MultiCheckboxField 
        :options="weekDays" 
        v-model="formValues.outboundDays" 
        @input="blurDays(); checkOther('out'); checkSelectAll('out')"
        :required="true"
      />
        <label v-if="otherDaySelectedOut" class="mr-2 pr-2 pb-0.5">
          <input 
            type="checkbox" 
            @input="toggleOutboundWeekend('Saturday')"
            :checked="formValues.outboundDays.includes('Saturday')"
          />
          Saturday
        </label>
        <label v-if="otherDaySelectedOut" class="mr-2 pr-2 pb-0.5">
          <input 
            type="checkbox" 
            @input="toggleOutboundWeekend('Sunday')"
            :checked="formValues.outboundDays.includes('Sunday')"
          />
          Sunday
        </label>
    </div>
    <div v-if="formValues.outboundDays?.length > 0 ">
      <div class="grid grid-cols-2 gap-7">
        <ValidationProvider rules="required" name="end time" v-slot="{ errors }">
          <TimeField
            :defaultValue="getDefaultValueForTime('schoolEndTime', defaultValue.schoolEndTime)"
            :disableDefaultValueUpdates="defaultValueComponentUpdatedMap['schoolEndTime']"
            @defaultValueUsed="defaultValueUsed('schoolEndTime')"
            class="w-full"
            v-model="formValues.schoolEndTime"
            @input="subtractDepartureTime('schoolEndTime', 10)"
          >
            School or Site End Time
          </TimeField>
          <span
            class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
            name="error-text">
            {{ errors[0] }}
          </span>
        </ValidationProvider>
        <div>
          <ValidationProvider rules="required" name="intended departure time" v-slot="{ errors }">
            <TimeField
              :defaultValue="getDefaultValueForTime(undefined, defaultValue.departureTime)"
              :disableDefaultValueUpdates="defaultValueComponentUpdatedMap['departureTime']"
              @defaultValueUsed="defaultValueUsed('departureTime')"
              class="w-full"
              v-model="formValues.departureTime"
            >
              Intended Departure Time
            </TimeField>
            <span
              class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
              name="error-text">
              {{ errors[0] }}
            </span>
          </ValidationProvider>
          <span class="text-xs">
            Default to depart 10 minutes after the school or program release time.
          </span>
        </div>
      </div>
        <div class="flex flex-col"
        :key="transOptionsOutKey">
          <label class="text-sm font-bold mt-2"
            id="Drop-off">
            <input
              type="checkbox"
              :disabled="formValues.diffDropoffTimes"
              @change="toggleDropOff()"
              :checked="formValues.diffDropoffAddress"
            />
            Check here if the drop-off address (school/facility to home) different from the home address.
          </label>
          <StudentAddress
            class="-mt-4"
            v-if="diffDropoffAddress && !formValues.diffDropoffTimes"
            v-model="formValues.newDropoffAddress"
            :label="'New Drop-Off Address'"
            :defaultValue="defaultDropoffAddress"
            :disableDefaultValueUpdates="defaultValueComponentUpdatedMap['diffDropoffAddress']"
            @defaultValueUsed="defaultValueUsed('diffDropoffAddress')"
          />
          <label class="text-sm font-bold mt-2">
            <input 
              type="checkbox" 
              :checked="formValues.diffDropoffTimes"
              @change="toggleDifferentDays('out')"
            />
            Check here if some days have a different end time or drop-off address
          </label>
          <div
            v-if="formValues.diffDropoffTimes"
            class="flex flex-col"
          >
            <DifferentTransOptions
              :daysList="formValues.outboundDays"
              v-model="formValues.outboundDayDifferent"
              direction="out"
              :defaultValue="defaultValueOutboundDayDifferent"
              :disableDefaultValueUpdates="defaultValueComponentUpdatedMap['diffDropoffDaysTransOptions']"
              @defaultValueUsed="defaultValueUsed('diffDropoffDaysTransOptions')"
            />
          </div>
        </div>
      <TextArea
        v-model="formValues.outboundInfo"
      >
        Specific Pickup Information for OUTbound Route (FROM school/site)
      </TextArea>
      <span class="text-xs">
        ex. PICKUP DOOR at school/site.
      </span>
      </div>
      <div class="grid grid-cols-2 gap-7 mt-4 ">
        <div>
          <SpecialNeedsSelection
            class="text-sm font-bold pb-2"
            v-model="formValues.specialNeeds"
            :defaultValues="defaultValue.specialNeeds"
            :disableDefaultValueUpdates="defaultValueComponentUpdatedMap['specialNeeds']"
            @defaultValueUsed="defaultValueUsed('specialNeeds')"
            :defaultAide="defaultValue.specialNeeds?.aide"
            @input="specialChange(formValues.specialNeeds.needsList)"
          >
            Equipment and Special Needs
          </SpecialNeedsSelection>
        </div>
        <div class="text-sm font-bold flex flex-col gap-1">
          <ValidationProvider rules="required" name="student release" v-slot="{ errors }">
            <RadioField
              :options="studentRelease"
              v-model="formValues.studentRelease"
              :alignment="'col'"
            >
              Student Release
            </RadioField>
            <span
              class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
              name="error-text">
              {{ errors[0] }}
            </span>
          </ValidationProvider>
        </div>
      </div>
    
    <TextArea 
      v-model="formValues.medicalInfo"
    >
      Medical and/or IEP Information Details Relevant to Transportation
    </TextArea>
    <TextArea 
      v-model="formValues.additionalInfo"
    >
      Additional Transportation Information
    </TextArea>
    <!-- TransDetails: {{ formValues }} -->
    <!-- selectedSchool: {{ selectedSchool }} -->
    <!-- defaultValue: {{ defaultValue }} -->
    <SuggestedSchoolForm
      ref="suggestedSchoolForm"
      :schoolList="schoolList"
      @select="onSelectSuggestedSchool"
    />
  </div>
</template>

<script>
import StudentAddress from './StudentAddress.vue'
import SchoolAddressDisplay from './SchoolAddressDisplay.vue'
import SuggestedSchoolForm from './SuggestedSchoolList/SuggestedSchoolForm.vue'
import DropdownLists from './DropdownLists.json'
import cloneDeep from 'clone-deep'

import CalendarField from './formComponents/CalendarField.vue'
import InputError from './formComponents/InputError.vue'
import MultiCheckboxField from './formComponents/MultiCheckboxField.vue'
import TimeField from './formComponents/TimeField.vue'
import TextArea from './formComponents/TextArea.vue'
import RadioField from './formComponents/RadioField.vue'
import SpecialNeedsSelection from './SpecialNeedsSelection.vue'
import DifferentTransOptions from './DifferentTransOptions.vue'
import Spinner from './Spinner.vue'

import requestFormService from '../services/formSubmission/requestForm.js'

import time from '../services/utils/time.js'
import user from '../services/user/user.js'
import { isObject, isNil, isArray, isString, isBoolean, reduce, every } from 'lodash'
//import counter from '../services/utils/counter.js'

const ALL_SUB_COMPONENTS = [
  'schoolAddress',
  'startTime',
  'arrivalTime',
  'diffPickupAddress',
  'diffPickupDaysTransOptions',
  'schoolEndTime',
  'departureTime',
  'diffDropoffAddress',
  'diffDropoffDaysTransOptions',
  'specialNeeds',
]

function createComponentsUpdatedMap(newValue = false) {
  return reduce(
    ALL_SUB_COMPONENTS,
    (accum, componentKey) => {
      accum[componentKey] = newValue
      return accum
    },
    {}
  )
}

export default {
  components: {
    StudentAddress,
    SchoolAddressDisplay,
    CalendarField,
    InputError,
    MultiCheckboxField,
    TimeField,
    TextArea,
    RadioField,
    SpecialNeedsSelection,
    DifferentTransOptions,
    SuggestedSchoolForm,
    Spinner,
  },
  name: 'TransDetails',
  props: {
    studentHomeAddress: undefined,
    attemptedSubmit: undefined,
    disableDefaultValueUpdates: Boolean,
    defaultValue: undefined,
    selectedProgram: undefined,
    selectedGrade: undefined,
    selectedSchoolYear: undefined,
    /*includeNotSpecifiedStudentRelease: {
      default: false,
      type: Boolean,
    },*/
    startDateSchoolYearWarning: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      defaultValueComponentUpdatedMap: createComponentsUpdatedMap(),

      leadTime: 1,
      transOptionsInKey: 0,
      transOptionsOutKey: 0,
      daysIsEdited: false,
      otherSchool: false,
      schoolIsEdited: false,
      inboundDays: [],
      outboundDays: [],
      startDate: '',
      startTime: '',
      arrivalTime: '',
      schoolEndTime: '',
      departureTime: '',
      allInDaysSelected: false,
      allOutDaysSelected: false,
      formValues: {},
      schoolList: [],
      weekDays: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Other',
      ],
      selectedSchool: '',
      schoolCode: '',
      schoolDistrictId: undefined,
      schoolName: '',
      schoolAddress: 
        {
          addressLabel: 'School Address',
          street: '',
          city: '',
          state: '',
          zip_code: '',
        },
      diffPickupAddress: false,
      diffDropoffAddress: false,
      defaultDropoffAddress: '',
      defaultPickupAddress: '',
      defaultSchoolAddress: undefined,
      buildingProgramTimes: undefined,
      tomorrow() {
        const today = new Date()
        const tomorrow = new Date(today)
        tomorrow.setDate(tomorrow.getDate() + 1)

        var month = tomorrow.getMonth() + Number(1)
        if (month < 10) {
          month = '0' + month
        }
        var date = tomorrow.getDate()
        if (date < 10) {
          date = '0' + date
        }
        return tomorrow.getFullYear() + '-' + month + '-' + date
      },
      minRequestDate() {
        const today = new Date()
        const endDate = new Date(today)

        for(let i = 1; i <= this.leadTime;){
          endDate.setDate(endDate.getDate() + 1)
          if(!(endDate.getDay() == 6 || endDate.getDay() == 0)){
            i++
          }
        }

        var month = endDate.getMonth() + Number(1)
        if (month < 10) {
          month = '0' + month
        }
        var date = endDate.getDate()
        if (date < 10) {
          date = '0' + date
        }
        return endDate.getFullYear() + '-' + month + '-' + date
      },
      specialNeeds: [],
      specialNeedsComponentKey: 0,
      boosterSeatPrevValue: false,
      boosterSeatOverride: false,
      suppressBoosterSeatWatch: false,

      skipTimeAdjustments: {},

      defaultValueInboundDayDifferentIntercept: undefined,
      defaultValueOutboundDayDifferentIntercept: undefined,

      displayLoadingBuildings: false,

      specialNeedsOptions: DropdownLists.specialNeeds,
      studentRelease: DropdownLists.studentRelease,
      aides: DropdownLists.Aides,
    }
  },
  methods: {
    setLeadTime(leadTime){
      this.leadTime = leadTime
    },
    formatTime(initTime) {
      var HH = initTime.substring(0, 2)
      var MM = initTime.substring(3, 5)
      return HH + ':' + MM     
    },
    checkSelectAll(direction) {
      if (direction === 'out' && this.allOutDaysSelected) {
        if (!this.formValues.outboundDays.includes('Other') && this.formValues.outboundDays.length < 5) {
          this.allOutDaysSelected = false
        }
      }
      if (direction === 'in' && this.allInDaysSelected) {
        if (!this.formValues.inboundDays?.includes('Other') && this.formValues.inboundDays?.length < 5) {
          this.allInDaysSelected = false
        }
      }
    },
    toggleDifferentPickup() {
      this.formValues.diffPickupTimes = !this.formValues.diffPickupTimes
      if (this.formValues.diffPickupTimes == false) {
        this.formValues.inboundDayDifferent = []
      }
    },
    toggleDifferentDropoff() {
      this.formValues.diffDropoffTimes = !this.formValues.diffDropoffTimes
      if (this.formValues.diffDropoffTimes == false) {
        this.formValues.outboundDayDifferent = []
      }
    },
    checkOther(dir) {
      if (dir === 'in') {
        if (!this.formValues.inboundDays?.includes('Other')) {
          if (this.formValues.inboundDays?.includes('Saturday')) {
            this.formValues.inboundDays?.splice(this.formValues.inboundDays?.indexOf('Saturday'), 1)
          }
          if (this.formValues.inboundDays?.includes('Sunday')) {
            this.formValues.inboundDays?.splice(this.formValues.inboundDays?.indexOf('Sunday'), 1)
          }
        }
        if (this.formValues.inboundDays?.length < 1) {
          this.formValues.inboundDayDifferent = []
        }
      }
      else {
        if (!this.formValues.outboundDays.includes('Other')) {
          if (this.formValues.outboundDays.includes('Saturday')) {
            this.formValues.outboundDays.splice(this.formValues.outboundDays.indexOf('Saturday'), 1)
          }
          if (this.formValues.outboundDays.includes('Sunday')) {
            this.formValues.outboundDays.splice(this.formValues.outboundDays.indexOf('Sunday'), 1)
          }
        }
        if (this.formValues.outboundDays.length < 1) {
          this.formValues.outboundDayDifferent = []
        }
      }
    },
    toggleInboundWeekend(day) {
      if (this.formValues.inboundDays?.includes(day)) {
        let index = this.formValues.inboundDays?.indexOf(day)
        this.formValues.inboundDays?.splice(index, 1)
      }
      else {
        this.formValues.inboundDays?.push(day)
      }
    },
    toggleOutboundWeekend(day) {
      if (this.formValues?.outboundDays?.includes(day)) {
        let index = this.formValues?.outboundDays?.indexOf(day)
        this.formValues?.outboundDays?.splice(index, 1)
      }
      else {
        this.formValues?.outboundDays?.push(day)
      }
    },
    specialChange(value) {
      if (this.suppressBoosterSeatWatch) {
        this.suppressBoosterSeatWatch = false
      }
      if (value !== undefined) {
        this.$emit('boosterSeatSelected', value.indexOf('infoBoosterSeat') > -1)
      }
    },
    setSchoolData() {
      this.formValues.schoolName = this.formValues.school?.name
      this.formValues.schoolAddress = this.formValues.school?.address
      this.formValues.schoolCode = this.formValues.school?.code
      this.formValues.schoolDistrictId = this.formValues.school?.district
    },
    schoolExists() {
      if (this.selectedSchool && this.schoolList) {
        if (this.schoolList.map(a => a.name).includes(this.selectedSchool.name)) {
          this.formValues.isNewSchool = false
          return true;
        }
        else {
          this.formValues.isNewSchool = true
          return false;
        }
      }
    },
    blurDays() {
      this.daysIsEdited = true
    },
    resetReviewed() {
      this.$emit('setReviewed', false)
    },
    updateValue() {
      this.$emit('input', this.formValues)
    },
    togglePickup() {
      this.diffPickupAddress = !this.diffPickupAddress
      this.formValues.diffPickupAddress = this.diffPickupAddress
    },
    toggleDropOff() {
      this.diffDropoffAddress = !this.diffDropoffAddress
      this.formValues.diffDropoffAddress = this.diffDropoffAddress
    },
    toggleDifferentDays(direction) {
      if (direction == 'in') {
        this.formValues.diffPickupAddress = this.diffPickupAddress
        this.formValues.diffPickupTimes = !this.formValues.diffPickupTimes
        this.transOptionsInKey += 1
        if (this.formValues.diffPickupTimes === false) {
          this.formValues.inboundDayDifferent = []
        }
        else {
          this.defaultValueInboundDayDifferentIntercept = this.formValues.inboundDays.map(
            dayName => ({
              'day': dayName,
              'programTime': this.formValues.startTime,
              'transportTime': this.formValues.arrivalTime,
              'diffAddress': true,
              'address': cloneDeep(this.studentHomeAddress),
            })
          )
        }
      }
      else {
        this.formValues.diffDropoffAddress = this.diffDropoffAddress
        this.formValues.diffDropoffTimes = !this.formValues.diffDropoffTimes
        this.transOptionsOutKey += 1
        if (this.formValues.diffDropoffTimes === false) {
          this.formValues.outboundDayDifferent = []
        }
        else {
          this.defaultValueOutboundDayDifferentIntercept = this.formValues.outboundDays.map(
            dayName => ({
              'day': dayName,
              'programTime': this.formValues.schoolEndTime,
              'transportTime': this.formValues.departureTime,
              'diffAddress': true,
              'address': cloneDeep(this.studentHomeAddress),
            })
          )
        }
      }
    },
    isAfterStartDate() {
      let start = new Date(this.formValues?.startDate)
      let end = new Date(this.formValues?.endDate)
      return start.getTime() > end.getTime()
    },
    isAfterToday() {
      let date = new Date(this.formValues?.startDate)
      let newDate = new Date()
      return date.getTime() <= newDate.getTime()
    },
    blurSchoolInput() {
      this.schoolIsEdited = true
    },
    toggleOther() {
      this.selectedSchool = { "id": '', "code": '', "district": '', "name": '', "address": { "street": '', "apartment": null, "city": '', "state": '', "zip_code": '' } }
      this.otherSchool = !this.otherSchool
    },
    // checkInboundSelectAll() {
    //   if (this.inboundDays) {
    //     let b = this.inboundDays.slice(0, 5)
    //     let a = this.weekDays.slice(0, 5)
    //     // checking if each value of a is strictly equal to the corresponding value of
    //     if (a.every((val, index) => val === b[index])) {
    //       this.allInDaysSelected = true
    //     } else {
    //       this.allInDaysSelected = false
    //     }
    //   }
    // },
    // checkOutboundSelectAll() {
    //   if (this.outboundDays) {
    //     let b = this.outboundDays.slice(0, 5)
    //     let a = this.weekDays.slice(0, 5)
    //     if (a.every((val, index) => val === b[index])) {
    //       this.allOutDaysSelected = true
    //     } else {
    //       this.allOutDaysSelected = false
    //     }
    //   }
    // },
    toggleAllInbound() {
      for (var i = 0; i < this.weekDays.length-1; i++) {
        if (this.allInDaysSelected) {
          if (!this.formValues.inboundDays?.includes(this.weekDays[i])) {
            this.formValues.inboundDays?.splice(i, 0, this.weekDays[i])
          }
        }
        else {
          this.formValues.inboundDays = []
          this.formValues.inboundDayDifferent = []
        }
      }
    },
    toggleAllOutbound() {
      for (var i = 0; i < this.weekDays.length-1; i++) {
        if (this.allOutDaysSelected) {
          if (!this.formValues.outboundDays.includes(this.weekDays[i])) {
            this.formValues.outboundDays.splice(i, 0, this.weekDays[i])
          }
        }
        else {
          this.formValues.outboundDays = []
          this.formValues.outboundDayDifferent = []
        }
      }
    },
    minutesOff(initTime, minutes = -5) {
      var date = new Date()
      var HH = initTime.substring(0, 2)
      var MM = Number(initTime.substring(3, 5)) + minutes
      date.setHours(HH, MM)
      var newHH = date.getHours()   < 10 ? '0' + date.getHours()   : date.getHours()
      var newMM = date.getMinutes() < 10 ? '0' + date.getMinutes() : date.getMinutes()
      return newHH + ':' + newMM
    },
    subtractArrivalTime(fieldKey, minutes = -5) {
      if (this.skipTimeAdjustments[fieldKey]) {
        this.skipTimeAdjustments[fieldKey] = false
        return
      }

      if (this.formValues.startTime) {
        this.formValues.arrivalTime = this.minutesOff(this.formValues.startTime, minutes)
      } else {
        this.formValues.arrivalTime = ''
      }
    },
    subtractDepartureTime(fieldKey, minutes = -5) {
      if (this.skipTimeAdjustments[fieldKey]) {
        this.skipTimeAdjustments[fieldKey] = false
        return
      }

      if (this.formValues.schoolEndTime) {
        this.formValues.departureTime = this.minutesOff(this.formValues.schoolEndTime, minutes)
      } else {
        this.formValues.departureTime = ''
      }
    },
    getDefaultValueForTime(fieldKey, value) {
      if (fieldKey && value !== undefined) {
        this.skipTimeAdjustments[fieldKey] = true
      }
      return time.toInputField(value)
    },
    getUnixTime(baseTime) {
      if (baseTime) {
        var date = new Date()
        var HH = baseTime.substring(0, 2)
        var MM = Number(baseTime.substring(3, 5))
        date.setHours(HH, MM)
        return date.getTime()
      } else {
        return ''
      }
    },
    getAllBuildingsAndSetDefaultTransDetails() {
      this.displayLoadingBuildings = true
      requestFormService.getBuildingFromServerForce()
        .then(
          result => {
            this.displayLoadingBuildings = false
            this.schoolList = result.data
          },
          error => {
            this.displayLoadingBuildings = false
            console.error(error)
          }
        )
      this.setDefaultTransDetails()
    },
    updateBuildingSelection() {
      this.displayLoadingBuildings = true
      requestFormService.getBuildingFromServerForce()
        .then(result => {
          this.displayLoadingBuildings = false
          this.schoolList = result.data
          let schoolObj = this.schoolList.find(obj => obj.name == this.schoolName)
          this.selectedSchool = schoolObj
          this.setSchool()
        },
          error => {
            this.displayLoadingBuildings = false
            console.error(error)
          })
    },
    setSchool() {
      if (this.selectedSchool && this.$refs.schoolAddressDisplay !== undefined) {
        this.$refs.schoolAddressDisplay.setAddress({
          street: this.selectedSchool?.address?.street,
          city: this.selectedSchool?.address?.city,
          state: this.selectedSchool?.address?.state,
          zip_code: this.selectedSchool?.address?.zip_code,
        })
      }
      this.schoolName = this.selectedSchool?.name
        ? this.selectedSchool.name
        : this.selectedSchool
      this.schoolCode = this.selectedSchool?.code
      this.schoolDistrictId = this.selectedSchool?.district
      this.schoolAddress.street = this.selectedSchool?.address?.street
      this.schoolAddress.city = this.selectedSchool?.address?.city
      this.schoolAddress.state = this.selectedSchool?.address?.state
      this.schoolAddress.zip_code = this.selectedSchool?.address?.zip_code

      this.getCurrentBuildingProgramTime(
        this.selectedSchool,
        this.selectedProgram,
        this.selectedGrade
      )
      this.formValues.school = this.selectedSchool ? this.selectedSchool : ''
      this.setSchoolData()
      if(this.formValues.isNewSchool) { // only need to validate when adding a new school
        this.resetReviewed(false)
      }
      this.updateValue()

      //this.$refs.suggestedSchoolForm.showSuggestedSchools(this.selectedSchool)
    },
    onSchoolAddressLoseFocus() {
      this.$refs.suggestedSchoolForm.showSuggestedSchools(this.selectedSchool, this.formValues[0]?.schoolAddress[0])
    },
    onSelectSuggestedSchool(dbId) {
      let selectedSchool = this.schoolList.find(school => school.id === dbId)
      this.selectedSchool = selectedSchool
      this.setSchool()
    },
    getCurrentBuildingProgramTime(selectedSchool, selectedProgram, selectedGrade) {
      if (
        selectedSchool &&
        selectedProgram &&
        selectedGrade &&
        (!this.arrivalTime || !this.schoolEndTime) &&
        selectedSchool.id
      ) {
        if (
          this.buildingProgramTimes === undefined ||
          this.buildingProgramTimes.selectedSchool != selectedSchool ||
          this.buildingProgramTimes.selectedProgram != selectedProgram ||
          this.buildingProgramTimes.selectedGrade != selectedGrade
        ) {
          var promise = requestFormService.getBuildingProgramTime(selectedSchool.id)
            .then(buildingProgramTimes => {
              var program = buildingProgramTimes.find(
                elem =>
                  elem.program.id == selectedProgram &&
                  elem.from_grade <= selectedGrade &&
                  elem.to_grade >= selectedGrade
              )
              if (program) {
                this.arrivalTime = program.start_time
                this.departureTime = program.end_time
                this.startTime = this.minutesOff(this.arrivalTime, 10)
                this.schoolEndTime = this.minutesOff(this.departureTime, -10)
              }
            })
          this.buildingProgramTimes = {
            selectedSchool: selectedSchool,
            selectedProgram: selectedProgram,
            selectedGrade: selectedGrade,
            data: promise,
          }
        }
      }
    },
    setEndDate() {
      if (this.isSummerSession()) {
        if (this.defaultValue.endDate) {
          if (new Date(this.minRequestDate()) < new Date(this.defaultValue.endDate)) {
            this.formValues.endDate = this.defaultValue.endDate
          }
          else {
            this.formValues.endDate = this.minRequestDate()
          }
        }
      }
      else {
        this.formValues.endDate = undefined
      }
    },
    isSummerSession() {
      // This depends on the schoolYears map in dropdownLists.json
      // Will work for now but may need to be updated if we change how 
      // we store school years in the future
      return this.selectedSchoolYear?.includes('sum')
    },
    setDefaultTransDetails() {
      if (this.disableDefaultValueUpdates) {
        return
      }

      if (!this.checkSchema(this.defaultValue)) {
        this.setEndDate()
        return
      }
      this.formValues = cloneDeep(this.defaultValue)

      this.selectedSchool = this.formValues?.['school']
      this.defaultSchoolAddress = this.selectedSchool?.address
      this.setSchool()
      delete this.formValues['school']
      this.inboundDays = this.formValues.inboundDays ? this.formValues.inboundDays : []
      this.outboundDays = this.formValues.outboundDays ? this.formValues.outboundDays : []
      this.startTime = this.formValues['startTime']
      this.schoolEndTime = this.formValues['schoolEndTime']
      this.arrivalTime = this.formValues['arrivalTime']
      this.departureTime = this.formValues['departureTime']
      this.defaultDropoffAddress = this.formValues['newDropoffAddress']
      this.defaultPickupAddress = this.formValues['newPickupAddress']
      this.formValues.diffDropoffAddress = this.diffDropoffAddress = !!(this.defaultDropoffAddress)
      this.formValues.diffPickupAddress = this.diffPickupAddress = !!(this.defaultPickupAddress)
      this.formValues.inboundDayDifferent = this.defaultValue.inboundDayDifferent
      this.formValues.outboundDayDifferent = this.defaultValue.outboundDayDifferent
      this.formValues.diffPickupTimes = !!(this.defaultValue.diffPickupTimes)
      this.formValues.diffDropoffTimes = !!(this.defaultValue.diffDropoffTimes)
      if (this.defaultValue.startDate) {
        if (new Date(this.minRequestDate()) < new Date(this.defaultValue.startDate)) {
          this.formValues.startDate = this.defaultValue.startDate
        }
        else {
          this.formValues.startDate = this.minRequestDate()
        }
      }
      this.setEndDate()
    },
    checkSchema(obj) {
      if (!isObject(obj)) {
        console.warn(`Invalid default value data type in TransDetails, ${typeof obj}`)
        return false
      }

      if (!(isNil(obj.school) || isObject(obj.school))) {
        console.warn(`Invalid default value data type in TransDetails.school, ${typeof obj.school}`)
        delete obj.school
      }
      if (!(isNil(obj.inboundDays) || isArray(obj.inboundDays))) {
        console.warn(`Invalid default value data type in TransDetails.inboundDays, ${typeof obj.inboundDays}`)
        delete obj.inboundDays
      }
      if (!(isNil(obj.outboundDays) || isArray(obj.outboundDays))) {
        console.warn(`Invalid default value data type in TransDetails.outboundDays, ${typeof obj.outboundDays}`)
        delete obj.outboundDays
      }
      if (!(isNil(obj.inboundDayDifferent) || isArray(obj.inboundDayDifferent))) {
        console.warn(`Invalid default value data type in TransDetails.inboundDayDifferent, ${typeof obj.inboundDayDifferent}`)
        delete obj.inboundDayDifferent
      }
      if (!(isNil(obj.outboundDayDifferent) || isArray(obj.outboundDayDifferent))) {
        console.warn(`Invalid default value data type in TransDetails.outboundDayDifferent, ${typeof obj.outboundDayDifferent}`)
        delete obj.outboundDayDifferent
      }
      if (!(isNil(obj.startTime) || isString(obj.startTime))) {
        console.warn(`Invalid default value data type in TransDetails.startTime, ${typeof obj.startTime}`)
        delete obj.startTime
      }
      if (!(isNil(obj.schoolEndTime) || isString(obj.schoolEndTime))) {
        console.warn(`Invalid default value data type in TransDetails.schoolEndTime, ${typeof obj.schoolEndTime}`)
        delete obj.schoolEndTime
      }
      if (!(isNil(obj.arrivalTime) || isString(obj.arrivalTime))) {
        console.warn(`Invalid default value data type in TransDetails.arrivalTime, ${typeof obj.arrivalTime}`)
        delete obj.arrivalTime
      }
      if (!(isNil(obj.diffDropoffAddress) || isBoolean(obj.diffDropoffAddress))) {
        console.warn(`Invalid default value data type in TransDetails.diffDropoffAddress, ${typeof obj.diffDropoffAddress}`)
        delete obj.diffDropoffAddress
      }
      if (!(isNil(obj.diffPickupAddress) || isBoolean(obj.diffPickupAddress))) {
        console.warn(`Invalid default value data type in TransDetails.diffPickupAddress, ${typeof obj.diffPickupAddress}`)
        delete obj.diffPickupAddress
      }
      if (!(isNil(obj.newDropoffAddress) || isObject(obj.newDropoffAddress))) {
        console.warn(`Invalid default value data type in TransDetails.newDropoffAddress, ${typeof obj.newDropoffAddress}`)
        delete obj.newDropoffAddress
      }
      if (!(isNil(obj.newPickupAddress) || isObject(obj.newPickupAddress))) {
        console.warn(`Invalid default value data type in TransDetails.newPickupAddress, ${typeof obj.newPickupAddress}`)
        delete obj.newPickupAddress
      }
      if (!(isNil(obj.diffDropoffTimes) || isBoolean(obj.diffDropoffTimes))) {
        console.warn(`Invalid default value data type in TransDetails.diffDropoffTimes, ${typeof obj.diffDropoffTimes}`)
        delete obj.diffDropoffTimes
      }
      if (!(isNil(obj.diffPickupTimes) || isBoolean(obj.diffPickupTimes))) {
        console.warn(`Invalid default value data type in TransDetails.diffPickupTimes, ${typeof obj.diffPickupTimes}`)
        delete obj.diffPickupTimes
      }
      if (!(isNil(obj.startDate) || isString(obj.startDate))) {
        console.warn(`Invalid default value data type in TransDetails.startDate, ${typeof obj.startDate}`)
        delete obj.startDate
      }
      if (!(isNil(obj.endDate) || isString(obj.endDate))) {
        console.warn(`Invalid default value data type in TransDetails.endDate, ${typeof obj.endDate}`)
        delete obj.endDate
      }

      return true
    },
    setBoosterSeatOverride(value) {
      if (this.boosterSeatOverride) {
        if (value) {
          this.setBoosterSeat(true, true)
        }
        else {
          this.setBoosterSeat(this.boosterSeatPrevValue, true)
        }
      }
      else {
        if (value) {
          if (this.formValues.specialNeeds === undefined) {
            this.formValues.specialNeeds = { needsList: [] }
          }
          this.boosterSeatPrevValue = (this.formValues.specialNeeds.needsList.indexOf('infoBoosterSeat') > -1)
          if (this.boosterSeatPrevValue === false) {
            this.setBoosterSeat(true, true)
          }
        }
        else {
          //do nothing
        }
      }
      this.boosterSeatOverride = value
    },
    setBoosterSeatPrevValue(value) {
      this.boosterSeatPrevValue = value
    },
    setBoosterSeat(value, suppressWatch) {
      this.suppressBoosterSeatWatch = suppressWatch
      if (value) {
        if (this.formValues.specialNeeds === undefined) {
          this.formValues.specialNeeds.needsList = []
        }
        let index = this.formValues.specialNeeds.needsList.indexOf('infoBoosterSeat')
        if (index === -1) {
          this.formValues.specialNeeds.needsList.push('infoBoosterSeat')
        }
      }
      else {
        let index = this.formValues.specialNeeds?.needsList.indexOf('infoBoosterSeat')
        if (index !== undefined && index > -1) {
          this.formValues.specialNeeds?.needsList?.splice(index, 1)
        }
      }
      this.formValues.specialNeedsComponentKey += 1
    },
    defaultValueUsed(componentKey) {
      this.defaultValueComponentUpdatedMap[componentKey] = true
    },
    resetComponentsUpdated() {
      this.defaultValueComponentUpdatedMap = createComponentsUpdatedMap()
      this.$refs.suggestedSchoolForm.resetPreviousData()
    },
  },
  computed:{
    otherDaySelectedIn() {
      return this.formValues?.inboundDays?.includes('Other')
    },
    otherDaySelectedOut() {
      return this.formValues?.outboundDays?.includes('Other')
    },
    allComponentsUpdated() {
      return every(this.defaultValueComponentUpdatedMap)
    },
    defaultValueInboundDayDifferent() {
      if (this.defaultValueInboundDayDifferentIntercept) {
        return this.defaultValueInboundDayDifferentIntercept
      }
      return this.defaultValue.inboundDayDifferent
    },
    defaultValueOutboundDayDifferent() {
      if (this.defaultValueOutboundDayDifferentIntercept) {
        return this.defaultValueOutboundDayDifferentIntercept
      }
      return this.defaultValue.outboundDayDifferent
    },
  },
  async mounted() {
    this.resetComponentsUpdated()
    await user.ready()

    this.getAllBuildingsAndSetDefaultTransDetails()
    this.studentRelease = DropdownLists.studentRelease
    this.updateValue()
  },
  watch: {
    selectedSchool(value) {
      this.formValues.school = value
      this.formValues.schoolName = value?.name
    },
    formValues() {
      this.setSchoolData()
      this.updateValue()
    },
    defaultValue() {
      this.setDefaultTransDetails()
    },
    selectedProgram() {
      this.getCurrentBuildingProgramTime(
        this.selectedSchool,
        this.selectedProgram,
        this.selectedGrade
      )
    },
    selectedGrade() {
      this.getCurrentBuildingProgramTime(
        this.selectedSchool,
        this.selectedProgram,
        this.selectedGrade
      )
    },
    selectedSchoolYear() {
      this.setEndDate()
    },
    /*includeNotSpecifiedStudentRelease(value) {
      if (value) {
        this.studentRelease = DropdownLists.studentRelease
        this.studentRelease['n/a'] = 'Not Specified'
      }
      else {
        this.studentRelease = DropdownLists.studentRelease
      }
    },*/
    specialNeeds: {
      deep: true,
      handler(value) {
        if (this.suppressBoosterSeatWatch) {
          this.suppressBoosterSeatWatch = false
          return
        }
        if (value.needsList !== undefined) {
          this.$emit('boosterSeatSelected', value.needsList.indexOf('infoBoosterSeat') > -1)
        }
        this.formValues.specialNeeds.needsList = value
      }
    },
  },
}
</script>
